/**
 * Cart Management System - Moon Moon Agency v2.1
 * Shopify cart with AJAX updates, custom events, and UI sync
 */

/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const CART_CONFIG = {
	// API Settings
	debounceDelay: 500,
	apiTimeout: 10000,

	// Selectors
	selectors: {
		cart: '[data-cart], .mm-cart',
		cartWrapper: '[data-cart-wrapper]',
		cartProducts: '[data-cart-products]',
		cartBubbles: '[data-cart-count-bubble]',
		addToCartForm: '[data-type="add-to-cart-form"]',
		cartClose: '[data-cart-close]',
		checkoutBtn: '[data-checkout-btn]',
		drawerCart: '[data-drawer-cart]',
		cartItemById: '[data-cart-item-id="{{id}}"]',
		lineIndex: '[data-line-index="{{index}}"]',
		itemQuantity: '[data-item-quantity]',
		freeProducts: '[data-free-products]'
	},

};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */
// Use global debounce from mm_utils.js

// Cart Event Emitter
const cartEmit = (event, detail = {}, cancelable = false) => {
	const eventData = {
		timestamp: window.getTimestamp?.() || new Date().toLocaleString(),
		event,
		data: detail,
		source: 'cart'
	};
	
	return document.dispatchEvent(new CustomEvent(`cart:${event}`, {
		detail: eventData,
		cancelable,
		bubbles: true
	}));
};

// DOM Selectors Cache
const DOM = {
	get cart() { return document.querySelector(CART_CONFIG.selectors.cart); },
	get cartSection() { return document.getElementById(this.cart?.id || ''); },
	get cartProducts() { return this.cartSection?.querySelector(CART_CONFIG.selectors.cartProducts); },
	get cartWrapper() { return document.querySelector(CART_CONFIG.selectors.cartWrapper); },
	get cartBubbles() { return document.querySelectorAll(CART_CONFIG.selectors.cartBubbles); },
	
	cartItem: (index) => document.querySelector(CART_CONFIG.selectors.lineIndex.replace('{{index}}', index)),
	
	cartQuantity: (index) => DOM.cartItem(index)?.querySelector(CART_CONFIG.selectors.itemQuantity)
};

/*********************************************************************************************
 * 🔌 CORE CART API
 */
const CartAPI = {
	async request(method, path, body = null) {
		cartEmit('api-start', { method, path });

		const baseUrl = window.Shopify?.routes?.root || '/';
		const url = baseUrl + path;

		const isFormData = (typeof FormData !== 'undefined') && body instanceof FormData;

		const config = { method };

		// 👉 IMPORTANT : n'ajoute pas Content-Type si c'est du FormData
		if (!isFormData) {
			config.headers = { 'Content-Type': 'application/json' };
		}

		if (body && method !== 'GET') {
			config.body = isFormData ? body : JSON.stringify(body);
		}

		try {
			const response = await fetch(url, config);
			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: ${response.statusText}`);
			}

			const data = await response.json();

			if (data.status && data.status >= 400) {
				throw new Error(data.message || data.description || 'Shopify API Error');
			}

			cartEmit('api-success', { method, path, itemCount: data.item_count });
			this.updateUI(data);
			return data;
		} catch (error) {
			cartEmit('api-error', { method, path, error: error.message });
			throw error;
		}
	},

	/**
	 * Updates cart UI elements with new data
	 */
	updateUI(data) {
		// Update cart count
		const count = data.item_count || 0;
		DOM.cartBubbles.forEach(bubble => {
			bubble.classList.remove('mm-none');
			bubble.textContent = '[' + count + ']';
		});
		cartEmit('count-updated', { count });

		// Update cart content if sections exist
		if (data.sections && DOM.cart) {
			const sectionId = DOM.cart.id.replace('shopify-section-', '');
			if (data.sections[sectionId]) {
				this.refreshDOM(data.sections[sectionId]);
			}
		}
	},

	/**
	 * Refreshes cart DOM with new HTML and reinitializes components
	 */
	refreshDOM(html) {
		const temp = document.createElement('div');
		temp.innerHTML = html;
		
		const newCart = temp.querySelector(CART_CONFIG.selectors.drawerCart);
		const currentCart = DOM.cartSection?.querySelector(CART_CONFIG.selectors.drawerCart);
		
		if (newCart && currentCart) {
			currentCart.innerHTML = newCart.innerHTML;
			// Re-init Splide via SplideManager (hook progress bar + arrows visibility inclus).
			// SplideManager skip les elements deja montes (idempotent).
			if (typeof window.SplideManager?.init === 'function') {
				window.SplideManager.init();
			} else if (typeof Splide !== 'undefined') {
				// Fallback : mount basique (perd la progress bar dynamique)
				document.querySelectorAll('.mm-drawer-cart .splide').forEach(function(element) {
					if (element._splide) return;
					try {
						const cfgRaw = element.dataset.splide;
						const cfg = cfgRaw ? JSON.parse(cfgRaw) : {};
						new Splide(element, cfg).mount();
					} catch (error) {
						window.log('warn', 'Cart', 'Failed to initialize Splide:', { element, error });
					}
				});
			}
			// Reinitialize event listeners for new DOM elements
			if (typeof window.initAddToCartForms === 'function') {
				window.initAddToCartForms();
			}
			if (typeof window.initCartQuantityListeners === 'function') {
				window.initCartQuantityListeners();
			}
			// Reinitialize promo code listeners
			if (typeof initPromoCode === 'function') {
				initPromoCode();
			}
			cartEmit('refreshed');
		}
	}
};

/*********************************************************************************************
 * 🛒 CART DATA MANAGEMENT
 */
const Cart = {
	// Focus trap state
	focusTrap: {
		isActive: false,
		triggerElement: null,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null
	},

	/**
	 * Setup focus trap for cart drawer
	 */
	setupFocusTrap(cartWrapper, triggerElement) {
		const focusableElements = cartWrapper.querySelectorAll(
			'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
		);

		if (focusableElements.length === 0) return;

		this.focusTrap.triggerElement = triggerElement;
		this.focusTrap.firstFocusable = focusableElements[0];
		this.focusTrap.lastFocusable = focusableElements[focusableElements.length - 1];

		// Handle Tab and Escape keys
		this.focusTrap.handleKeyDown = (e) => {
			// Escape key closes cart
			if (e.key === 'Escape') {
				e.preventDefault();
				this.close();
				this.focusTrap.triggerElement?.focus();
				return;
			}

			// Tab key traps focus
			if (e.key !== 'Tab') return;

			if (e.shiftKey) {
				// Shift + Tab: going backwards
				if (document.activeElement === this.focusTrap.firstFocusable) {
					e.preventDefault();
					this.focusTrap.lastFocusable?.focus();
				}
			} else {
				// Tab: going forwards
				if (document.activeElement === this.focusTrap.lastFocusable) {
					e.preventDefault();
					this.focusTrap.firstFocusable?.focus();
				}
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;

		// Focus first element
		setTimeout(() => this.focusTrap.firstFocusable?.focus(), 100);

		window.log('info', 'Cart', 'Focus trap activated');
	},

	/**
	 * Remove focus trap
	 */
	removeFocusTrap() {
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.triggerElement = null;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;
			window.log('info', 'Cart', 'Focus trap removed');
		}
	},

	async get() {
		cartEmit('fetch-start');
		const data = await CartAPI.request('GET', 'cart.js');
		cartEmit('fetched', { itemCount: data.item_count, totalPrice: data.total_price });
		return data;
	},

	/**
	 * Open cart
	 */
	open(triggerElement) {
		if (!cartEmit('before-open', {}, true)) return;

		this.get().catch(error => window.log('error', 'Cart', 'Failed to fetch cart on open', { error: error.message }));

		const wrapper = DOM.cartWrapper;
		const html = document.documentElement;

		if (wrapper && html) {
			wrapper.removeAttribute('inert');
			wrapper.classList.add('mm-dc-is-open');
			html.classList.add('stop-scrolling');

			// Stop Lenis smooth-scroll (desktop) — sinon le scroll trap derriere le drawer continue
			if (typeof window.LenisManager?.stop === 'function') window.LenisManager.stop();

			// Inert background content for screen readers (WCAG 2.4.3)
			document.querySelectorAll('main, header, footer').forEach(el => {
				if (!wrapper.contains(el)) el.inert = true;
			});

			// Hide overlays
			['shopify-chat', 'axeptio_overlay'].forEach(id => {
				const elem = document.getElementById(id);
				if (elem) elem.style.display = 'none';
			});

			// Fallback to active element so focus is always restored on close (WCAG 2.4.3)
			const trigger = triggerElement || document.activeElement;
			this.setupFocusTrap(wrapper, trigger);

			cartEmit('opened');
		}
	},

	/**
	 * Close cart
	 */
	close() {
		if (!cartEmit('before-close', {}, true)) return;

		const wrapper = DOM.cartWrapper;
		const html = document.documentElement;

		if (wrapper) {
			wrapper.classList.remove('mm-dc-is-open');
			wrapper.setAttribute('inert', '');
		}
		if (html) html.classList.remove('stop-scrolling');

		// Reprend Lenis (desktop)
		if (typeof window.LenisManager?.resume === 'function') window.LenisManager.resume();

		// Restore background content (mirror of open: only main/header/footer)
		document.querySelectorAll('main, header, footer').forEach(el => el.inert = false);

		// Remove focus trap
		this.removeFocusTrap();

		// Show overlays
		['shopify-chat', 'axeptio_overlay'].forEach(id => {
			const elem = document.getElementById(id);
			if (elem) elem.style.display = '';
		});

		cartEmit('closed');
	},

	/**
	 * Get quantity
	 */
	getQuantity(lineIndex) {
		const elem = DOM.cartQuantity(lineIndex);
		return elem ? parseInt(elem.dataset.itemQuantity, 10) || 0 : 0;
	},

	/**
	 * Extract comprehensive item data from cart DOM
	 */
	getItemData(lineIndex) {
		const itemEl = DOM.cartItem(lineIndex);
		if (!itemEl) return null;

		const data = {
			lineIndex: parseInt(lineIndex, 10),
			variantId: itemEl.dataset.cartItemId,
			quantity: this.getQuantity(lineIndex),
			maxQuantity: itemEl.dataset.maxQuantity,
			properties: {},
			variants: {},
			metadata: {}
		};

		// Extract properties from DOM elements (if displayed)
		const propertyElements = itemEl.querySelectorAll('[data-property]');
		propertyElements.forEach(el => {
			const propName = el.dataset.property;
			const propValue = el.textContent.trim();
			if (propName && propValue) {
				data.properties[propName] = propValue;
			}
		});

		// Extract variant options (if displayed)
		const variantElements = itemEl.querySelectorAll('[data-variant-option]');
		variantElements.forEach(el => {
			const optionName = el.dataset.variantOption;
			const optionValue = el.textContent.trim();
			if (optionName && optionValue) {
				data.variants[optionName] = optionValue;
			}
		});

		// Add any other metadata from data attributes
		Object.keys(itemEl.dataset).forEach(key => {
			if (!['cartItemId', 'lineIndex', 'maxQuantity'].includes(key)) {
				data.metadata[key] = itemEl.dataset[key];
			}
		});

		return data;
	},

	/**
	 * Set quantity
	 */
	setQuantity(lineIndex, newQty, skipEvent = false) {
		const elem = DOM.cartQuantity(lineIndex);
		if (!elem || newQty < 0) return;

		const oldQty = parseInt(elem.dataset.itemQuantity, 10) || 0;
		elem.dataset.itemQuantity = newQty;
		if (elem.tagName === 'SELECT') {
			elem.value = String(newQty);
		} else {
			elem.textContent = newQty;
		}

		if (!skipEvent) {
			cartEmit('quantity-changed', { lineIndex, oldQty, newQty });
		}
	},

	/**
	 * Update quantity
	 */
	async updateQuantity(lineIndex, newQty) {
		const itemEl = DOM.cartItem(lineIndex);
		const variantId = itemEl?.dataset.cartItemId;
		
		cartEmit('quantity-sync-start', { lineIndex, quantity: newQty, variantId });
		
		try {
			const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';
			
			// Shopify cart/change.js requires 1-based line index and proper sections format
			const requestBody = {
				line: parseInt(lineIndex, 10), // Should be 1-based from template
				quantity: newQty
			};
			
			// Add sections for bundled section rendering if available
			if (sectionId) {
				requestBody.sections = sectionId;
			}
			
			const data = await CartAPI.request('POST', 'cart/change.js', requestBody);
			
			await this.processFreeProducts(data);
			cartEmit('quantity-synced', { lineIndex, quantity: newQty, variantId });
			return data;
		} catch (error) {
			cartEmit('quantity-sync-error', { lineIndex, error: error.message });
			// Fallback: refresh cart to sync with server state
			this.get().catch(error => window.log('error', 'Cart', 'Failed to fetch cart on open', { error: error.message }));
			throw error;
		}
	},

	/**
	 * Atomic batch update : passe plusieurs lignes a quantite 0 en une seule requete.
	 * Utilise pour la suppression cascade d'un bundle (monture + verre + gravure).
	 */
	async removeMultipleByKey(keys) {
		const sectionId = DOM.cart?.id?.replace('shopify-section-', '') || '';
		const updates = {};
		for (const k of keys) updates[k] = 0;
		const body = { updates };
		if (sectionId) body.sections = sectionId;
		cartEmit('bundle-remove-start', { keys });
		try {
			const data = await CartAPI.request('POST', 'cart/update.js', body);
			cartEmit('bundle-removed', { keys, response: data });
			return data;
		} catch (e) {
			cartEmit('bundle-remove-error', { keys, error: e.message });
			throw e;
		}
	},

	/**
	 * Process free products
	 */
	async processFreeProducts(data) {
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';
		if (!data.sections?.[sectionId]) return data;

		const temp = document.createElement('div');
		temp.innerHTML = data.sections[sectionId];
		const freeProductsEl = temp.querySelector(CART_CONFIG.selectors.freeProducts);
		
		if (!freeProductsEl?.children.length) return data;

		const updates = {};
		Array.from(freeProductsEl.children).forEach(el => {
			const variantId = el.dataset.variantId;
			const shouldHave = el.dataset.freeProduct === 'true';
			const hasIt = !!document.querySelector(CART_CONFIG.selectors.cartItemById.replace('{{id}}', variantId));

			if (hasIt && !shouldHave) {
				updates[variantId] = 0;
				cartEmit('free-product-removing', { variantId });
			} else if (!hasIt && shouldHave) {
				updates[variantId] = 1;
				cartEmit('free-product-adding', { variantId });
			}
		});

		if (Object.keys(updates).length > 0) {
			try {
				// Shopify cart/update.js expects updates object and optional sections
				const requestBody = { updates };
				
				if (sectionId) {
					requestBody.sections = sectionId;
				}
				
				await CartAPI.request('POST', 'cart/update.js', requestBody);
				cartEmit('free-products-updated', { updates });
			} catch (error) {
				cartEmit('free-products-error', { error: error.message });
			}
		}

		return data;
	}
};

/*********************************************************************************************
 * 🔢 QUANTITY CONTROLS
 */
const debouncedUpdate = window.debounce((lineIndex) => {
	const quantity = Cart.getQuantity(lineIndex);
	Cart.updateQuantity(lineIndex, quantity);
}, CART_CONFIG.debounceDelay);

/*********************************************************************************************
 * 🛏️ EXPORTED FUNCTIONS
 */

/**
 * Increases product quantity by 1
 */
window.mmIncreaseProductQuantity = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);
	const maxValue = itemData?.maxQuantity;
	const max = (maxValue && !isNaN(parseInt(maxValue, 10))) ? parseInt(maxValue, 10) : Infinity;
	const newQty = current + 1;

	const eventData = {
		...itemData,
		oldQty: current,
		newQty,
		max,
		action: 'increase'
	};

	if (!cartEmit('before-quantity-increase', eventData, true)) return;
	
	if (newQty > max) {
		cartEmit('quantity-limit-reached', eventData);
		return;
	}

	Cart.setQuantity(lineIndex, newQty);
	cartEmit('quantity-increased', { ...eventData, newQty });
	debouncedUpdate(lineIndex);
};

/**
 * Decreases product quantity by 1, removes if quantity would be 0
 */
window.mmDecreaseProductQuantity = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);
	const newQty = current - 1;

	const eventData = {
		...itemData,
		oldQty: current,
		newQty,
		action: 'decrease'
	};

	if (!cartEmit('before-quantity-decrease', eventData, true)) return;
	
	if (newQty < 1) {
		// Delegue a mmDestroyProductFromCart pour que la cascade de l'emballage cadeau
		// (cf. _for_line_key, non geree par Shopify) soit appliquee aussi sur ce chemin.
		window.mmDestroyProductFromCart(lineIndex);
		return;
	}

	Cart.setQuantity(lineIndex, newQty);
	cartEmit('quantity-decreased', eventData);
	debouncedUpdate(lineIndex);
};

/**
 * Sets product quantity directly (used by the select-based qty picker).
 * If newQty is 0, removes the item; otherwise sets the new quantity.
 */
window.mmSetProductQuantity = (lineIndex, newQty) => {
	const target = parseInt(newQty, 10);
	if (isNaN(target) || target < 0) return;
	if (target === 0) {
		window.mmDestroyProductFromCart(lineIndex);
		return;
	}
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);
	const eventData = { ...itemData, oldQty: current, newQty: target, action: 'set' };
	if (!cartEmit('before-quantity-set', eventData, true)) return;
	Cart.setQuantity(lineIndex, target);
	cartEmit('quantity-changed', eventData);
	debouncedUpdate(lineIndex);
};

/**
 * Removes product completely from cart
 */
window.mmDestroyProductFromCart = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);

	const eventData = {
		...itemData,
		oldQty: current,
		action: 'destroy'
	};

	if (!cartEmit('before-item-remove', eventData, true)) return;

	// Note : les nested cart lines (parent_id natif Shopify) sont automatiquement supprimees
	// lorsque le parent est retire — pas besoin de cascade cote client.
	// Mais l'emballage cadeau est attache via property _for_line_key (custom link, pas
	// parent_id natif), donc Shopify ne le cascade PAS — on doit le supprimer atomiquement
	// dans la meme requete via cart/update.js sinon il reste orphelin au panier.
	const itemEl = DOM.cartItem(lineIndex);
	const parentKey = itemEl?.dataset.lineKey;
	const wrapLineKey = itemEl?.querySelector('[data-giftwrap-block]')?.dataset.wrapLineKey;

	if (parentKey && wrapLineKey) {
		Cart.removeMultipleByKey([parentKey, wrapLineKey])
			.then(() => cartEmit('item-removed', eventData))
			.catch((err) => window.log('error', 'Cart', 'Cascade remove with giftwrap failed', { error: err.message }));
		return;
	}

	Cart.setQuantity(lineIndex, 0);
	cartEmit('item-removed', eventData);
	debouncedUpdate(lineIndex);
};

/*********************************************************************************************
 * 🏗️ GLOBAL FUNCTIONS
 */
window.mmOpenCart = (triggerElement) => Cart.open(triggerElement);
window.mmCloseCart = () => Cart.close();
window.mmGetCart = () => Cart.get();
// Rafraichit l'UI cart a partir d'une reponse cart/add.js ou cart/change.js incluant `sections`.
window.mmRefreshCartUI = (data) => CartAPI.updateUI(data);
// ID de section du cart drawer (pour requete avec `sections=...`)
window.mmGetCartSectionId = () => DOM.cart?.id?.replace('shopify-section-', '') || '';
window.mmGetCartItemData = (lineIndex) => Cart.getItemData(lineIndex);

/**
 * Publie le standard event Shopify Web Pixels `product_added_to_cart` apres un
 * fetch /cart/add.js reussi. Sans cet appel manuel, Shopify n'emet PAS l'event
 * pour les ajax /cart/add.js (uniquement pour les form submits natifs), donc
 * Addingwell / GA4 / Meta ne voient pas les ajouts depuis collection/PDP/quickview.
 *
 * @param {object} addResponse - reponse JSON de /cart/add.js (soit l'item, soit { items: [...] } en bulk)
 *
 * SAFE : entierement isole dans un try/catch interne + guard sur Shopify.analytics.publish.
 * Si le pixel crash ou est absent, le caller ne voit AUCUN side-effect.
 */
window.mmPublishCartAdd = (addResponse) => {
	try {
		if (!window.Shopify?.analytics?.publish) return;
		const items = addResponse?.items || (addResponse?.variant_id ? [addResponse] : []);
		if (!items.length) return;
		const currency = window.Shopify?.currency?.active || 'EUR';
		for (const item of items) {
			window.Shopify.analytics.publish('product_added_to_cart', {
				cartLine: {
					cost: {
						totalAmount: { amount: (item.final_line_price || 0) / 100, currencyCode: currency },
					},
					merchandise: {
						id: `gid://shopify/ProductVariant/${item.variant_id}`,
						title: item.variant_title || '',
						price: { amount: (item.final_price || 0) / 100, currencyCode: currency },
						product: {
							id: `gid://shopify/Product/${item.product_id}`,
							title: item.product_title || '',
							type: item.product_type || '',
							vendor: item.vendor || '',
							url: item.url || '',
						},
						sku: item.sku || '',
					},
					quantity: item.quantity || 1,
				},
			});
		}
	} catch (err) {
		window.log('warn', '🛒 [CART_PIXEL]', 'product_added_to_cart publish failed (non-blocking)', { error: err.message });
	}
};

/*********************************************************************************************
 * 🏗️ UTILITY FUNCTIONS (MERGED ABOVE)
 */
window.updateCartCount = (value) => CartAPI.updateUI({ item_count: value });

// Cart UI Event Listeners
const initCartEventListeners = () => {
	// Close cart functionality
	document.addEventListener('click', (e) => {
		if (e.target.closest('[data-cart-close]')) {
			e.preventDefault();
			Cart.close();
		}

		// Checkout button loading
		if (e.target.closest('[data-checkout-btn]')) {
			const btn = e.target.closest('[data-checkout-btn]');
			//btn.classList.add('mm-loading');
			cartEmit('checkout-loading', { element: btn });
		}

		// Discount removal
		const discountButton = e.target.closest('[data-discount-remove]');
		if (discountButton) {
			e.preventDefault();
			const discountTitle = discountButton.getAttribute('data-discount-title');
			if (discountTitle) {
				window.removeDiscount(discountTitle);
			}
		}
	});
};

/*********************************************************************************************
 * 🛒 Add to Cart Forms Handler
 */
window.initAddToCartForms = () => {
	document.querySelectorAll('[data-type="add-to-cart-form"]:not([data-cart-handler-attached])').forEach(form => {
		form.setAttribute('data-cart-handler-attached', 'true');
		form.addEventListener('submit', async (e) => {
			e.preventDefault();

			const submitButton = form.querySelector('[type="submit"]');
			const formData = new FormData(form);

			if (!formData.get('id')) {
				window.log('error', 'Cart', 'No variant ID found in form', { form });
				return;
			}

			// Add loading state
			if (submitButton) {
				submitButton.classList.add('mm-loading');
				submitButton.disabled = true;
			}

			// Extract all form data including properties and options
			const extractedData = {
				variantId: formData.get('id'),
				quantity: parseInt(formData.get('quantity') || 1, 10),
				properties: {},
				options: {},
				formEntries: {}
			};

			// Collect all form entries
			for (const [key, value] of formData.entries()) {
				extractedData.formEntries[key] = value;

				// Extract properties (custom fields)
				if (key.startsWith('properties[')) {
					const propName = key.match(/properties\[(.+)\]/)?.[1];
					if (propName && value) {
						extractedData.properties[propName] = value;
					}
				}

				// Extract options (variant selections like size, color)
				if (key.startsWith('option')) {
					extractedData.options[key] = value;
				}
			}

			cartEmit('add-to-cart-start', extractedData);

			try {
				const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

				// Add sections for cart update if available
				if (sectionId) {
					formData.append('sections', sectionId);
				}

				const response = await CartAPI.request('POST', 'cart/add.js', formData);
				cartEmit('add-to-cart-success', {
					...extractedData,
					response: response
				});

				/* Publie le standard event Shopify Web Pixels (Addingwell/GA4/Meta).
				 * Sans cet appel, Shopify n'emet pas product_added_to_cart pour les
				 * ajax /cart/add.js -> tracking ads silencieux. */
				window.mmPublishCartAdd?.(response);

				// Process free products after adding to cart
				await Cart.processFreeProducts(response);

				// Auto-open cart after successful add
				Cart.open();

			} catch (error) {
				cartEmit('add-to-cart-error', {
					...extractedData,
					error: error.message
				});
				window.log('error', 'Cart', 'Add to cart failed', {
					error: error.message,
					...extractedData
				});
			} finally {
				// Remove loading state
				if (submitButton) {
					submitButton.classList.remove('mm-loading');
					submitButton.disabled = false;
				}
			}
		});
	});
};

/*********************************************************************************************
 * 🎯 ACCESSIBILITY - Quantity Selector Event Listeners (replace inline onclick)
 */
window.initCartQuantityListeners = function initCartQuantityListeners() {
	// Cart decrease buttons
	document.querySelectorAll('[data-cart-decrease]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartDecrease;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmDecreaseProductQuantity(lineIndex);
			if (priceContainer) window.LoaderManager.loadThis(priceContainer);
		});
	});

	// Cart increase buttons
	document.querySelectorAll('[data-cart-increase]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartIncrease;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmIncreaseProductQuantity(lineIndex);
			if (priceContainer) window.LoaderManager.loadThis(priceContainer);
		});
	});

	// Cart remove buttons (delete item)
	document.querySelectorAll('[data-cart-remove]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartRemove;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmDestroyProductFromCart(lineIndex);
			if (priceContainer) {
				window.LoaderManager.loadThis(priceContainer);
			} else {
				// For free products, load the button itself
				window.LoaderManager.loadThis(btn);
			}
		});
	});

	// Cart quantity select (0-9, 0 = remove)
	document.querySelectorAll('[data-cart-quantity-select]').forEach((select) => {
		if (select.dataset.qtyHandlerAttached) return;
		select.dataset.qtyHandlerAttached = 'true';
		select.addEventListener('change', () => {
			const lineIndex = select.dataset.lineIndex;
			const newQty = parseInt(select.value, 10);
			window.mmSetProductQuantity(lineIndex, newQty);
		});
	});
}

/*********************************************************************************************
 * 💰 PROMO CODE MANAGEMENT
 */
function isDiscountAppliedToCart(cart, code) {
	// discount_codes couvre les codes niveau panier ET ligne (produit-specifiques, Buy X Get Y).
	// Plus fiable que cart_level_discount_applications, dont la forme differe entre cart.js
	// ({ title, ... }) et cart/update.js ({ amount, discount_application: { title } }).
	const normalizedCode = code.toLowerCase();
	return cart.discount_codes?.some(d => d.applicable && d.code.toLowerCase() === normalizedCode) || false;
}

function initPromoCode() {
	const applyBtn = document.querySelector('[data-action="apply-discount"]');
	const discountInput = document.querySelector('input[data-name="discount"]');

	window.log('info', 'Cart', 'Initializing promo code', { applyBtn: !!applyBtn, discountInput: !!discountInput });

	if (applyBtn && discountInput && !applyBtn.dataset.promoHandlerAttached) {
		applyBtn.dataset.promoHandlerAttached = 'true';
		applyBtn.addEventListener('click', (e) => {
			e.preventDefault();
			e.stopPropagation();
			window.log('info', 'Cart', 'Apply discount button clicked');
			applyDiscount();
		});
		discountInput.addEventListener('keypress', (e) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				e.stopPropagation();
				window.log('info', 'Cart', 'Enter key pressed in discount input');
				applyDiscount();
			}
		});
		// Hide error when user starts typing
		discountInput.addEventListener('input', () => {
			hidePromoError();
		});
		window.log('info', 'Cart', 'Promo code listeners attached successfully');
	}
}

async function applyDiscount() {
	window.log('info', 'Cart', 'applyDiscount function called');

	const input = document.querySelector('input[data-name="discount"]');
	const button = document.querySelector('[data-action="apply-discount"]');

	if (!input || !button) {
		window.log('error', 'Cart', 'Input or button not found', { input: !!input, button: !!button });
		return;
	}

	const code = input.value.trim();
	window.log('info', 'Cart', 'Discount code entered', { code });

	if (!code) {
		window.log('warn', 'Cart', 'Empty discount code');
		return;
	}

	// Disable button and show loading state
	button.disabled = true;
	button.classList.add('mm-loading');

	try {
		// Get cart state before applying discount
		window.log('info', 'Cart', 'Getting cart state before discount...');
		const cartBefore = await CartAPI.request('GET', 'cart.js');
		// discount_codes = codes saisis, niveau panier ET ligne (un code produit-specifique ou Buy X Get Y
		// n'apparait pas dans cart_level_discount_applications), hors reductions automatiques.
		const codesBefore = (cartBefore.discount_codes || []).filter(d => d.applicable).map(d => d.code);
		window.log('info', 'Cart', 'Cart before', {
			total: cartBefore.total_price,
			codes: codesBefore
		});

		// Check if this exact code is already applied
		const codeAlreadyApplied = codesBefore.some(c => c.toLowerCase() === code.toLowerCase());

		if (codeAlreadyApplied) {
			showPromoError(input.dataset.errorAlreadyApplied || 'Code already applied');
			window.log('info', 'Cart', 'This code is already applied', { code });
			input.value = ''; // Clear invalid code
			return;
		}

		// Check if another discount is already applied (Shopify allows only one)
		if (codesBefore.length > 0) {
			const existingDiscount = codesBefore[0];
			showPromoError(`${input.dataset.errorAlreadyApplied || 'Code already applied'} (${existingDiscount})`);
			window.log('info', 'Cart', 'Another discount already applied', { existing: existingDiscount, attempted: code });
			input.value = ''; // Clear invalid code
			return;
		}

		// Check if cart is empty
		if (cartBefore.item_count === 0) {
			showPromoError(input.dataset.errorEmptyCart || 'Cart is empty');
			window.log('info', 'Cart', 'Cart is empty');
			input.value = ''; // Clear invalid code
			return;
		}

		// Code cadeau (bloc "Code cadeau" de MM Cart) : le produit offert doit etre ajoute AVANT
		// le code, sinon Shopify n'alloue pas la reduction Buy X Get Y (cf. mm_cart_gift_code.js).
		const giftOffer = window.MMCartGiftCode?.getOffer(code);
		if (giftOffer) {
			const result = await window.MMCartGiftCode.apply(giftOffer, cartBefore);
			if (result.data) CartAPI.updateUI(result.data);

			if (!result.ok) {
				const giftErrors = {
					unavailable: input.dataset.errorGiftUnavailable,
					not_eligible: giftOffer.message || input.dataset.errorNotApplicable
				};
				showPromoError(giftErrors[result.reason] || input.dataset.errorText || 'Error applying discount code');
				input.value = '';
				return;
			}

			input.value = '';
			cartEmit('discount-applied', { code, giftVariantId: giftOffer.variantId });
			return;
		}

		// Apply discount via cart/update.js API
		window.log('info', 'Cart', 'Applying discount via API...', { code });

		// Get section ID for cart refresh
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

		const updatePayload = {
			discount: code
		};

		if (sectionId) {
			updatePayload.sections = sectionId;
		}

		const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updatePayload)
		});

		if (!response.ok) {
			throw new Error('Cart update failed');
		}

		const updateData = await response.json();
		window.log('info', 'Cart', 'Cart updated', { updateData });

		// Verify if discount was applied
		const cartAfter = await CartAPI.request('GET', 'cart.js');
		const codeApplied = isDiscountAppliedToCart(cartAfter, code);

		if (!codeApplied) {
			// Discount was not applied - code is invalid
			window.log('warn', 'Cart', 'Discount was not applied by Shopify', { code });

			// IMPORTANT: Force removal of invalid code from cart
			const cleanupPayload = {
				discount: '' // Empty string removes the discount
			};

			if (sectionId) {
				cleanupPayload.sections = sectionId;
			}

			try {
				await fetch(window.Shopify.routes.root + 'cart/update.js', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(cleanupPayload)
				});
				window.log('info', 'Cart', 'Invalid code cleaned from cart', { code });
			} catch (cleanupError) {
				window.log('error', 'Cart', 'Failed to clean invalid code', { error: cleanupError.message });
			}

			showPromoError(input.dataset.errorNotApplicable || 'Discount code not applicable');
			input.value = ''; // Clear invalid code
			return;
		}

		// Discount applied successfully, refresh UI
		window.log('info', 'Cart', 'Discount applied successfully');

		if (updateData.sections && updateData.sections[sectionId]) {
			CartAPI.refreshDOM(updateData.sections[sectionId]);
		} else {
			// Fallback: manual refresh
			if (DOM.cart) {
				const cartData = await CartAPI.request('GET', `cart.js?sections=${sectionId}`);
				if (cartData.sections && cartData.sections[sectionId]) {
					CartAPI.refreshDOM(cartData.sections[sectionId]);
				}
			}
		}

		// Clear input
		input.value = '';

		// Emit success event
		cartEmit('discount-applied', { code });
		window.log('info', 'Cart', 'Discount applied and cart refreshed');

	} catch (error) {
		window.log('error', 'Cart', 'Failed to apply discount', { error: error.message, stack: error.stack });
		showPromoError(input.dataset.errorText || 'Error applying discount code');
		input.value = ''; // Clear invalid code on error
	} finally {
		// Reset button state
		button.disabled = false;
		button.classList.remove('mm-loading');
	}
}

function showPromoError(message) {
	const errorElement = document.querySelector('[data-promo-error]');
	const input = document.querySelector('input[data-name="discount"]');

	if (errorElement) {
		errorElement.textContent = message;
		errorElement.classList.remove('mm-none');
	}

	if (input) {
		input.classList.add('error');
	}

	// Auto-hide after 5 seconds
	setTimeout(() => {
		hidePromoError();
	}, 5000);
}

function hidePromoError() {
	const errorElement = document.querySelector('[data-promo-error]');
	const input = document.querySelector('input[data-name="discount"]');

	if (errorElement) {
		errorElement.classList.add('mm-none');
		errorElement.textContent = '';
	}

	if (input) {
		input.classList.remove('error');
	}
}

window.removeDiscount = async function(code) {
	window.log('info', 'Cart', 'Removing discount', { code });

	const discountButton = document.querySelector(`[data-discount-code="${code.toLowerCase()}"] [data-discount-remove]`);

	if (discountButton) {
		// Add loading state
		discountButton.disabled = true;
		discountButton.style.opacity = '0.5';
	}

	try {
		// Get section ID for cart refresh
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

		// Remove discount via cart/update.js API with empty discount parameter
		window.log('info', 'Cart', 'Removing discount via API...', { code });

		const updatePayload = {
			discount: ''  // Empty string removes the discount
		};

		// Cadeaux rattaches au code : retires dans la meme requete (sinon ils repassent a plein tarif)
		const giftLineKeys = window.MMCartGiftCode?.getLineKeys(code) || [];
		if (giftLineKeys.length) {
			updatePayload.updates = Object.fromEntries(giftLineKeys.map(key => [key, 0]));
		}

		if (sectionId) {
			updatePayload.sections = sectionId;
		}

		const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updatePayload)
		});

		if (!response.ok) {
			throw new Error('Cart update failed');
		}

		const updateData = await response.json();
		window.log('info', 'Cart', 'Discount removal response', { updateData });

		// Verify discount was removed
		const cartAfter = await CartAPI.request('GET', 'cart.js');
		const discountStillPresent = isDiscountAppliedToCart(cartAfter, code);

		if (discountStillPresent) {
			window.log('error', 'Cart', 'Discount was not removed', { code });
			throw new Error('Discount removal failed');
		}

		window.log('info', 'Cart', 'Discount removed successfully', { code });

		// Refresh UI
		if (updateData.sections && updateData.sections[sectionId]) {
			CartAPI.refreshDOM(updateData.sections[sectionId]);
		} else {
			// Fallback: manual refresh
			if (DOM.cart) {
				const cartData = await CartAPI.request('GET', `cart.js?sections=${sectionId}`);
				if (cartData.sections && cartData.sections[sectionId]) {
					CartAPI.refreshDOM(cartData.sections[sectionId]);
				}
			}
		}

		// Emit success event
		cartEmit('discount-removed', { code });
		window.log('info', 'Cart', 'Discount removed and cart refreshed');

	} catch (error) {
		window.log('error', 'Cart', 'Failed to remove discount', { error: error.message, stack: error.stack });

		// Reset button state on error
		if (discountButton) {
			discountButton.disabled = false;
			discountButton.style.opacity = '1';
		}
	}
};

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	cartEmit('initializing');
	window.initCartQuantityListeners();
	initPromoCode();

	// Cart drawer starts inert (not keyboard-accessible when closed)
	const cartWrapperInit = document.querySelector(CART_CONFIG.selectors.cartWrapper);
	if (cartWrapperInit) cartWrapperInit.setAttribute('inert', '');

	Cart.get()
		.then(data => {
			cartEmit('initialized', { itemCount: data.item_count, totalPrice: data.total_price });
			initCartEventListeners();
			window.initAddToCartForms();
		})
		.catch(error => {
			window.log('error', 'Cart', 'Cart initialization failed', { error: error.message });
			cartEmit('initialization-error', { error: error.message });
		});
});

